import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAccess } from "@/context/AccessContext";
import { PowerballBall } from "./PowerballBall";
import { CalendarClock, RefreshCw, Trophy } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

export function LatestDraw() {
  const { active } = useAccess();
  const [latest, setLatest] = useState(null);
  const [next, setNext] = useState(null);
  const [sync, setSync] = useState(null);
  const [jackpot, setJackpot] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  const load = async () => {
    const [d, nx, sy, jp] = await Promise.all([
      api.get("/draws?limit=1"),
      api.get("/draws/schedule?count=1"),
      api.get("/draws/sync-status"),
      api.get("/jackpot").catch(() => ({ data: null })),
    ]);
    setLatest(d.data.draws[0]);
    setNext(nx.data.upcoming[0]);
    setSync(sy.data);
    setJackpot(jp.data);
  };

  useEffect(() => { load(); }, []);

  const refresh = async () => {
    setRefreshing(true);
    try {
      const { data } = await api.post("/draws/refresh");
      if (data.success) {
        toast.success(data.throttled ? "Checked less than a minute ago — already current" : `Refreshed. ${data.added} new draws. ${data.total} total.`);
        await load();
      } else {
        toast.error("Live refresh failed: " + (data.error || "unknown"));
      }
    } finally {
      setRefreshing(false);
    }
  };

  return (
    <section className="px-4 sm:px-8 lg:px-12 pt-12 pb-4 max-w-[1400px] mx-auto">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 bg-surface border border-white/5 rounded-xl p-6 sm:p-8 relative overflow-hidden">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <div className="label-caps">Most Recent Draw</div>
              <div className="font-heading text-3xl sm:text-4xl mt-1 text-parchment tracking-tight">
                {latest?.date || "Loading…"}
              </div>
            </div>
            {active && (
              <Button
                data-testid="refresh-draws-btn"
                onClick={refresh}
                disabled={refreshing}
                variant="ghost"
                className="text-parchment/70 hover:text-oracle hover:bg-white/5 label-caps px-2 sm:px-4 shrink-0"
              >
                <RefreshCw className={`w-4 h-4 sm:mr-2 ${refreshing ? "animate-spin" : ""}`} strokeWidth={1.5} />
                <span className="hidden sm:inline">Refresh Live</span>
              </Button>
            )}
          </div>

          <div className="mt-6 flex flex-wrap items-center gap-2 sm:gap-3">
            {latest?.whites?.map((n, i) => (
              <PowerballBall key={n + "-" + i} n={n} testid={`latest-white-${i}`} delay={i * 60} />
            ))}
            {latest && <div className="hidden sm:block text-parchment/30 font-heading text-3xl px-2">·</div>}
            {latest && <PowerballBall n={latest.powerball} red testid="latest-powerball" delay={5 * 60} />}
          </div>
          <div className="mt-5 flex items-center gap-2 font-body text-[11px] text-parchment/40" data-testid="auto-sync-status">
            <span className={`w-1.5 h-1.5 rounded-full ${sync?.pending ? "bg-oracle animate-pulse" : "bg-emerald-400"}`} />
            {sync?.pending
              ? `Awaiting ${sync.last_draw_date} results · auto-checking every 15 min`
              : `Auto-synced after every Mon · Wed · Sat draw${sync?.last_checked_at ? ` · checked ${new Date(sync.last_checked_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}` : ""}`}
          </div>
        </div>

        <div className="bg-surface border border-white/5 rounded-xl p-6 sm:p-8 relative">
          <div className="label-caps flex items-center gap-2"><CalendarClock className="w-3 h-3" strokeWidth={1.5} /> Next Draw</div>
          <div className="mt-2 font-heading text-3xl sm:text-4xl text-oracle tracking-tight" data-testid="next-draw-weekday">
            {next?.weekday || "—"}
          </div>
          <div className="font-body text-parchment/60 text-xs mt-2" data-testid="next-draw-label">
            {next ? `${next.label} · 10:59 PM ET` : ""}
          </div>
          {jackpot?.jackpot_text && (
            <div className="mt-5 pt-5 border-t border-white/5" data-testid="jackpot-tracker">
              <div className="label-caps flex items-center gap-2"><Trophy className="w-3 h-3 text-oracle" strokeWidth={1.5} /> Estimated Jackpot</div>
              <div data-testid="jackpot-amount" className="font-heading text-4xl sm:text-5xl text-parchment tracking-tight mt-1 leading-none animate-jackpot-glow">
                {jackpot.jackpot_text}
              </div>
              {jackpot.cash_value_text && (
                <div className="font-body text-parchment/50 text-xs mt-2" data-testid="jackpot-cash">Cash value {jackpot.cash_value_text}</div>
              )}
              <div className="font-body text-[10px] text-parchment/30 mt-2">Source: powerball.com · updated {jackpot.fetched_at ? new Date(jackpot.fetched_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : ""}</div>
            </div>
          )}
          <div className="absolute top-4 right-4 w-16 h-16 rounded-full bg-pb_red/10 border border-pb_red/30 flex items-center justify-center">
            <div className="w-8 h-8 rounded-full bg-pb_red shadow-[0_0_18px_rgba(229,9,20,0.6)]" />
          </div>
        </div>
      </div>
    </section>
  );
}
